<script>
  /**
   * Progress Steps Component - Shows wizard progress
   * @prop {Array} steps - Array of {number, title} objects
   * @prop {number} currentStep - Current active step number
   */
  let { steps = [], currentStep = 1 } = $props();
</script>

<div class="progress-steps">
  {#each steps as step, index}
    {@const isCompleted = currentStep > step.number}
    {@const isCurrent = currentStep === step.number}
    {@const isActive = currentStep >= step.number}

    <div
      class="step"
      class:active={isActive}
      class:current={isCurrent}
      class:completed={isCompleted}
    >
      <div class="step-indicator">
        {#if isCompleted}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        {:else}
          <span>{step.number}</span>
        {/if}
      </div>
      <span class="step-title">{step.title}</span>
    </div>

    {#if index < steps.length - 1}
      <div class="step-connector" class:active={isCompleted}></div>
    {/if}
  {/each}
</div>

<style>
  .progress-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.5rem;
    gap: 0;
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    position: relative;
  }

  .step-indicator {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e5e7eb;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 600;
    transition: all 0.2s ease;
    border: 2px solid transparent;
  }

  .step-indicator svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
  }

  /* Current step - purple ring */
  .step.current .step-indicator {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    color: white;
    box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.2);
    transform: scale(1.1);
  }

  /* Completed step - green with white check */
  .step.completed .step-indicator {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
  }

  .step-title {
    font-size: 0.75rem;
    color: #9ca3af;
    text-align: center;
    max-width: 70px;
    line-height: 1.2;
    font-weight: 500;
    white-space: nowrap;
  }

  .step.current .step-title {
    color: #7c3aed;
    font-weight: 600;
  }

  .step.completed .step-title {
    color: #059669;
    font-weight: 500;
  }

  .step-connector {
    width: 40px;
    height: 2px;
    background: #e5e7eb;
    margin: 0 0.25rem;
    margin-bottom: 1.5rem;
    border-radius: 1px;
    transition: background 0.2s ease;
  }

  .step-connector.active {
    background: linear-gradient(90deg, #10b981, #34d399);
  }

  @media (min-width: 480px) {
    .progress-steps {
      padding: 0.5rem 1rem;
    }

    .step-indicator {
      width: 40px;
      height: 40px;
      font-size: 1rem;
    }

    .step-indicator svg {
      width: 20px;
      height: 20px;
    }

    .step-title {
      font-size: 0.8125rem;
      max-width: 90px;
    }

    .step-connector {
      width: 60px;
      margin: 0 0.5rem;
      margin-bottom: 1.75rem;
    }
  }

  @media (min-width: 640px) {
    .step-indicator {
      width: 44px;
      height: 44px;
    }

    .step-title {
      font-size: 0.875rem;
      max-width: 100px;
    }

    .step-connector {
      width: 80px;
      margin-bottom: 2rem;
    }
  }
</style>
